import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError, setAuthHeaderProvider } from '../api/client'
import { msalInstance } from '../auth/msal'
import { userAuthHeaders } from '../auth/userAuthHeaders'
import { useRealtime } from '../realtime/useRealtime'
import type { DashboardView, ScreenConfigDto } from './types'
import { TvDashboard } from './TvDashboard'

const TOKEN_STORAGE_KEY = 'tcpss.deviceToken'

interface ScreenCommand {
  id: number
  type: string
  payload?: string | null
}

/**
 * TV-visningen (kravspec afsnit 14-17).
 *
 * Kører uden almindeligt brugerlogin: Raspberry Pi-klienten åbner /tv?token=<device token>,
 * tokenet gemmes lokalt og bruges derefter til både REST og SignalR.
 * Adminpanelet kan forhåndsvise et dashboard med /tv?dashboard=<id>. Den bruger det
 * login, adminpanelet har i samme browser - lokalt eller Microsoft (beslutning 087).
 */
export function TvApp() {
  const [deviceToken, setDeviceToken] = useState<string | null>(null)
  const [previewDashboardId, setPreviewDashboardId] = useState<number | null>(null)
  const [pairingCode, setPairingCode] = useState<string | null>(null)
  const [screenConfig, setScreenConfig] = useState<ScreenConfigDto | null>(null)
  const [view, setView] = useState<DashboardView | null>(null)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [overlay, setOverlay] = useState<{ title: string; text?: string } | null>(null)
  const overlayTimer = useRef<number | null>(null)

  // --- Opstart: find device token eller preview-tilstand ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tokenFromUrl = params.get('token')
    const dashboardParam = params.get('dashboard')
    const pairParam = params.get('pair')

    // Koden til tilknytning (beslutning 075). Pi-agenten beder om den og aabner
    // siden med den; browseren viser den kun - den haandterer aldrig tokenet.
    // Agenten beder kun om en kode, naar den intet token har - er skaermen slettet
    // og tilknyttes igen, er et gemt token forældet og skal ikke vinde over koden.
    const pairing = !tokenFromUrl && !dashboardParam && pairParam !== null && /^[0-9]{6}$/.test(pairParam)
    if (pairing) {
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      setPairingCode(pairParam)
    }

    if (tokenFromUrl) {
      localStorage.setItem(TOKEN_STORAGE_KEY, tokenFromUrl)
      // Fjern tokenet fra adresselinjen, så det ikke står synligt på skærmen.
      params.delete('token')
      const query = params.toString()
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
    }

    const stored = tokenFromUrl ?? localStorage.getItem(TOKEN_STORAGE_KEY)
    if (dashboardParam) {
      setPreviewDashboardId(Number(dashboardParam))
    }
    setDeviceToken(stored)

    setAuthHeaderProvider(async (): Promise<Record<string, string>> => {
      if (!dashboardParam) {
        return stored ? { 'X-Device-Token': stored } : {}
      }

      // Forhåndsvisning fra Dashboards: samme login som adminpanelet (beslutning 087).
      // Før kendte den kun Microsoft-login og dev-login, så en lokal bruger fik
      // "Ingen forbindelse". I den her fane har ingen startet MSAL, så det gøres her.
      try {
        await msalInstance?.initialize()
      } catch {
        // Uden MSAL kan det lokale token stadig bruges.
      }
      return userAuthHeaders({ redirectOnInteraction: false })
    })
  }, [])

  // --- Hent konfiguration og dashboard ---
  const loadDashboard = useCallback(async () => {
    try {
      if (previewDashboardId) {
        setView(await api.get<DashboardView>(`/api/dashboards/${previewDashboardId}/view`))
        setConnectionError(null)
        return
      }

      let config = screenConfig
      if (!config) {
        config = await api.get<ScreenConfigDto>('/api/screens/me')
        setScreenConfig(config)
      }

      setView(await api.get<DashboardView>(`/api/screens/${config.screenId}/dashboard`))
      setConnectionError(null)
    } catch (err) {
      // En forhåndsvisning uden login er ikke en forbindelsesfejl - sig, hvad der mangler.
      if (previewDashboardId && err instanceof ApiError && err.status === 401) {
        setConnectionError('Du er ikke logget ind i denne browser. Log ind i adminpanelet, og tryk Forhåndsvis TV-visning igen.')
      } else if (previewDashboardId && err instanceof ApiError && err.status === 403) {
        setConnectionError('Du har ikke adgang til at se dette dashboard.')
      } else {
        setConnectionError(err instanceof Error ? err.message : 'Ingen forbindelse til serveren.')
      }
    }
  }, [previewDashboardId, screenConfig])

  useEffect(() => {
    if (deviceToken === null && previewDashboardId === null) {
      return
    }
    void loadDashboard()
  }, [deviceToken, previewDashboardId, loadDashboard])

  // Heartbeat sendes af Pi-agenten (tv-client/bin/tcpss-agent.py), ikke af browseren.
  // Agenten kender også TV'ets CEC-tilstand og enhedens version, og den bliver ved
  // med at melde sig selv selv om browseren skulle være crashet.
  // Se docs/DECISIONS.md beslutning 005.

  // --- Ny version efter en udrulning (beslutning 076) ---
  //
  // Siden ligger i service workerens cache. Uden det her fik en skærm først en ny
  // version at se, når browseren var genstartet TO gange: første gang blev den nye
  // version hentet i baggrunden, anden gang blev den vist. En TV-skærm har ingen, der
  // trykker F5 - så den spørger selv hvert 5. minut og genindlæser, når den nye version
  // har taget over.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return
    }

    const serviceWorker = navigator.serviceWorker
    // Kun hvis siden allerede kørte under en service worker. Ellers ville den allerførste
    // installation også give en genindlæsning, uden at der var noget nyt at vise.
    const hadController = Boolean(serviceWorker.controller)
    let reloading = false

    const onControllerChange = () => {
      if (!hadController || reloading) return
      reloading = true
      window.location.reload()
    }

    serviceWorker.addEventListener('controllerchange', onControllerChange)
    const timer = window.setInterval(() => {
      void serviceWorker.getRegistration().then((registration) => registration?.update())
    }, 5 * 60 * 1000)

    return () => {
      serviceWorker.removeEventListener('controllerchange', onControllerChange)
      window.clearInterval(timer)
    }
  }, [])

  // --- Fuld genindlæsning som fallback hvis realtid svigter ---
  useEffect(() => {
    const seconds = view?.refreshIntervalSeconds ?? 300
    const timer = window.setInterval(() => void loadDashboard(), Math.max(seconds, 30) * 1000)
    return () => window.clearInterval(timer)
  }, [view?.refreshIntervalSeconds, loadDashboard])

  const showOverlay = useCallback((title: string, text: string | undefined, seconds: number) => {
    setOverlay({ title, text })
    if (overlayTimer.current) window.clearTimeout(overlayTimer.current)
    overlayTimer.current = window.setTimeout(() => setOverlay(null), seconds * 1000)
  }, [])

  const handleCommand = useCallback(
    async (raw: unknown) => {
      const command = raw as ScreenCommand
      let error: string | undefined

      switch (command.type) {
        case 'reload_dashboard':
          await loadDashboard()
          break
        case 'reload_config':
          setScreenConfig(null)
          await loadDashboard()
          break
        case 'show_message': {
          const payload = command.payload ? (JSON.parse(command.payload) as { message: string; durationSeconds?: number }) : null
          showOverlay('Besked', payload?.message ?? '', payload?.durationSeconds ?? 30)
          break
        }
        case 'show_test_page':
          showOverlay('Testside', 'Skærmen fungerer og har forbindelse til serveren.', 20)
          break
        default:
          // restart_browser, restart_device, tv_on og tv_off udføres af Pi-agenten,
          // ikke af browseren. Den kvitterer selv for dem.
          return
      }

      if (screenConfig) {
        try {
          await api.post(`/api/screens/${screenConfig.screenId}/commands/${command.id}/ack`, { error })
        } catch {
          // Kvitteringen er ikke kritisk for visningen.
        }
      }
    },
    [loadDashboard, screenConfig, showOverlay]
  )

  const connection = useRealtime({
    enabled: Boolean(deviceToken) && !previewDashboardId,
    deviceToken: deviceToken ?? undefined,
    onDataChanged: () => void loadDashboard(),
    onCommand: (command) => void handleCommand(command)
  })

  const isOffline = connectionError !== null

  const title = useMemo(() => view?.dashboardName ?? 'Projektoversigt', [view])

  if (!deviceToken && !previewDashboardId && pairingCode) {
    return (
      <div className="tv-pair">
        <img className="tv-pair__logo" src="/logo/tc-logo-dark.png" alt="theodor christiansen" />
        <div className="tv-pair__label">Tilknyt skærmen</div>
        <div className="tv-pair__code">
          {pairingCode.slice(0, 3)} {pairingCode.slice(3)}
        </div>
        <div className="tv-pair__help">
          Gå til <strong>TV-skærme</strong> i adminpanelet, tryk <strong>Tilknyt med kode</strong>, og skriv
          koden. Skærmen skifter selv over, når den er tilknyttet.
        </div>
      </div>
    )
  }

  if (!deviceToken && !previewDashboardId) {
    return (
      <div className="tv">
        <div className="tv__overlay">
          <div>
            <div className="tv__overlay-title">Skærmen er ikke tilknyttet</div>
            <div className="tv__overlay-text">
              Åbn adressen med et device token: /tv?token=&lt;token fra adminpanelet&gt;
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <TvDashboard
        view={view}
        title={title}
        screenName={screenConfig?.name ?? view?.screenName ?? null}
        connection={connection}
        isOffline={isOffline}
        isPreview={previewDashboardId !== null}
      />

      {overlay && (
        <div className="tv__overlay">
          <div>
            <div className="tv__overlay-title">{overlay.title}</div>
            {overlay.text && <div className="tv__overlay-text">{overlay.text}</div>}
          </div>
        </div>
      )}

      {isOffline && !view && (
        <div className="tv__overlay">
          <div>
            <div className="tv__overlay-title">
              {previewDashboardId ? 'Forhåndsvisningen kunne ikke hentes' : 'Ingen forbindelse'}
            </div>
            <div className="tv__overlay-text">{connectionError}</div>
          </div>
        </div>
      )}
    </>
  )
}
